import { useRef, useState } from 'react';

import { useLocation, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme, useMediaQuery } from '@mui/material';
import { useFormik } from 'formik';

import { HOTELS, HOTELS_SEARCH } from '@shared/helpers/routes';
import billingBookingSchema from '@shared/components/BillingData/billingBookingSchema';
import { sendBuyForm } from './sendBuyForm';
import {
  updateHotelFormikValuesStep2,
  saveSearchDetails,
} from '@shared/redux/actions';
import BillingData from '@shared/components/BillingData';
import PaymentMethodSelector from '@shared/components/PaymentMethodSelector';
import GenericBookingLayout from '@shared/components/GenericBookingLayout';
import BookingDetail from '../BookingDetail';
import StepsWrapper from './StepsWrapper';
import { useBillingError } from '@shared/hooks/useBillingError';
import BookingValuationModalFlow from '../helpers/BookingValuationModalFlow';
import { useHotelBookingValuation } from '../helpers/useHotelBookingValuation';

import ActionButton from '@shared/components/Buttons/ActionButton';
import FormSection from '@shared/components/FormSection';
import Remarks from '../helpers/Remarks/Remarks';
import { hasValidContent } from '@shared/pages/Hotels/helpers/Remarks/hasValidContent';

const HotelsPayments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const user = useSelector(state => state.userInfo);
  const formikPaymentRef = useRef(null);
  const paymentSelectorRef = useRef(null);

  const hotelFormikValues = useSelector(state => state.hotelFormikValues);

  const {
    hotelDetails,
    search,
    subsidiary,
    seller,
    detailTransaction,
    id_integration,
    baseBookingBody,
    passengerDetails,
    storageKey,
    id_provider,
    urlToken,
    room,
    hotelCode,
    id_hotel,
    idCity,
    price: initialPrice,
    cancelationFee: initialCancelationFee,
    hotelsValidateResult: initialHotelsValidateResult,
  } = location.state ?? {};

  // 🔹 Estados locales
  const [infoCard, setInfoCard] = useState(null);
  const [cardData, setCardData] = useState();
  const [pricesDetail, setPricesDetail] = useState({});
  const [isLoadingPay, setIsLoadingPay] = useState(false);

  const {
    billingError,
    showBillingError,
    clearBillingError,
  } = useBillingError();

  // 🔹 Hook de valoración del hotel
  const {
    price,
    cancelationFee,
    valuationChanges,
    showValuationModal,
    showRetryModal,
    finalError,
    checkHotelValidationStep,
    dismissRetry,
    closeModalAndGoBack,
    closeModalAndContinue,
    hotelsValidateResult,
    priceRef,
  } = useHotelBookingValuation({
    id_integration,
    id_provider,
    urlToken,
    hotelCode,
    id_hotel,
    idCity,
    initialPrice,
    initialCancelationFee,
    initialHotelsValidateResult,
    id_transaction: room.id_transaction,
  });

  const compare =
    user?.user?.entity?.id === 23 || user?.user?.entity?.id === 46;

  const handleChangeInfoCard = data => setInfoCard(data);

  // 🔹 Formik
  const formik = useFormik({
    initialValues: hotelFormikValues?.step2 ?? {
      ownerCardName: '',
      ownerCardLastName: '',
      ownerDocument: '',
      afip: '',
      street: '',
      streetNumber: '',
      floor: '',
      province: '',
      department: '',
      postalCode: '',
      city: '',
    },
    validationSchema: billingBookingSchema({ isMobile, withEntity: false }),
    onSubmit: async () => {
      if (isLoadingPay) return;

      setIsLoadingPay(true);

      if (Object.keys(formik.errors).length) {
        setIsLoadingPay(false);
        return;
      }

      try {
        dispatch(updateHotelFormikValuesStep2(formik.values));

        if (!infoCard) {
          showBillingError({
            title: 'Hubo un error al verificar el método de pago.',
            message:
              'En caso de que persista el problema comuníquese con su sucursal más cercana.',
          });
          return;
        }

        // 🔍 Check booking valuation
        const check = await checkHotelValidationStep();
        if (!check?.ok) return;

        // 🧾 Enviar formulario
        await sendBuyForm({
          infoCard,
          showBillingError,
          baseBookingBody,
          subsidiary,
          search,
          pricesDetail,
          hotelDetails,
          user,
          values: formik.values,
          id_integration,
          detailTransaction,
          passengerDetails,
          history,
          dispatch,
          seller,
          room,
          total: priceRef?.current.details?.total,
          price: priceRef.current,
          cardData,
          cancelationFee,
          hotelsValidateResult,
        });
      } finally {
        setIsLoadingPay(false);
      }
    },
  });

  const hasRemarks =
    hasValidContent(room?.general_comments) ||
    hasValidContent(cancelationFee?.general_comments) ||
    hasValidContent(hotelsValidateResult?.comments) ||
    hasValidContent(hotelsValidateResult?.remarks);

  return (
    <>
      <GenericBookingLayout
        searchPath={HOTELS_SEARCH}
        storageKey={storageKey}
        expireTime={600000}
        pago
        operationType="compra"
        steps={[
          { title: 'Alojamiento', navigate: null, data: null },
          { title: 'Detalle de alojamiento', navigate: null, data: null },
          { title: 'Compra', navigate: null, data: null },
        ]}
        stepsWrapperComponent={<StepsWrapper />}
        formComponent={
          <>
            <PaymentMethodSelector
              ref={paymentSelectorRef}
              handleChangeInfoCard={handleChangeInfoCard}
              precio={priceRef.current?.details}
              setCardData={setCardData}
              cardData={cardData}
              idPlan={
                search?.city?.country?.toUpperCase() === 'ARGENTINA' ? 2 : 3
              }
              setPriceDetail={setPricesDetail}
              initialPriceDetail={pricesDetail}
              formikPaymentRef={formikPaymentRef}
            />
            <BillingData
              formik={formik}
              billingError={billingError}
              clearBillingError={clearBillingError}
              isLoadingPay={isLoadingPay}
              isMobile={isMobile}
              formikPaymentRef={formikPaymentRef}
              paymentSelectorRef={paymentSelectorRef}
            />

            {hasRemarks && (
              <FormSection title={'Observaciones'} mdVal={12}>
                <Remarks
                  room={room}
                  hotelsValidateResult={{
                    remarks: hotelsValidateResult?.remarks,
                    comments: hotelsValidateResult?.comments,
                  }}
                  buttonClose={false}
                  preview
                  cancelationFee
                />
              </FormSection>
            )}

            <ActionButton
              isLoading={isLoadingPay}
              onClick={async () => {
                if (isLoadingPay) return;

                const isPaymentValid = paymentSelectorRef?.current?.validatePayment?.();
                if (!isPaymentValid) return;

                if (formikPaymentRef?.current) {
                  await formikPaymentRef.current.submitForm();
                }

                formik.submitForm();
              }}
              buttonText="Confirmar"
            />
          </>
        }
        detailsComponent={
          <BookingDetail
            room={room}
            hotelDetails={hotelDetails}
            searchData={search}
            cancelationFee={cancelationFee}
            price={price}
            hotelsValidateResult={hotelsValidateResult}
            cardData={cardData}
            isPayment={true}
          />
        }
      />

      <BookingValuationModalFlow
        showChangesModal={showValuationModal}
        valuationChanges={valuationChanges}
        id_integration={id_integration}
        supplier={hotelsValidateResult?.supplier}
        id_product={baseBookingBody.id_product}
        isFromHotelBookingStyled={false}
        compare={compare}
        index={1}
        total_nights={search.totalNights}
        total_passangers={search.totalPassengers}
        check_in={search.checkIn}
        onCancelChanges={() => closeModalAndGoBack(history)}
        onConfirmChanges={() => {
          if (isLoadingPay) return;
          closeModalAndContinue(formik.submitForm);
        }}
        showRetryModal={showRetryModal}
        finalError={finalError}
        onRetry={dismissRetry}
        onAbort={() => {
          dispatch(saveSearchDetails(search));
          history.push(HOTELS);
        }}
      />
    </>
  );
};

export default HotelsPayments;
